import { Component, inject, Input, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ChecklistItem } from "@domain/project/checklist";
import {
  ChecklistItemMultipleChoice,
  ChecklistItemSecondaryDisplays,
  ChecklistItemSingleChoice,
} from "@domain/project/checklist-monitoring";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ChecklistItemMultipleChoiceComponent } from "@project/project-details/checklist/checklist-item-multiple-choice/checklist-item-multiple-choice.component";
import { ChecklistItemSecondaryDisplaysComponent } from "@project/project-details/checklist/checklist-item-secondary-displays/checklist-item-secondary-displays.component";
import { ChecklistItemSingleChoiceComponent } from "@project/project-details/checklist/checklist-item-single-choice/checklist-item-single-choice.component";
import { ChecklistItemComponent } from "@project/project-details/checklist/checklist-item/checklist-item.component";
import { filter, map, Observable } from "rxjs";

interface ChecklistOption {
  value: string;
  label: string;
}

@Component({
  selector: "app-checklist-monitoring",
  templateUrl: "./checklist-monitoring.component.html",
  styleUrls: ["./checklist-monitoring.component.scss"],
  imports: [
    UiKitModule,
    ReactiveFormsModule,
    ChecklistItemComponent,
    ChecklistItemSingleChoiceComponent,
    ChecklistItemMultipleChoiceComponent,
    ChecklistItemSecondaryDisplaysComponent,
  ],
  standalone: true,
})
export class ChecklistMonitoringComponent implements OnInit {
  private userService = inject(UserService);
  private formBuilder = inject(FormBuilder);
  private projectService = inject(ProjectService);

  @Input()
  project!: MonitoringProject;

  checklistForm!: FormGroup;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
  }

  public static getChecklistOptions(): Record<string, ChecklistOption[]> {
    return {
      networked: [
        {
          value: "separate",
          label: $localize`:@@checklistMonitoring.networked.option1:Separate network`,
        },
        {
          value: "integrated",
          label: $localize`:@@checklistMonitoring.networked.option2:Integrated network`,
        },
      ],
      centralStationIncluded: [
        {
          value: "deskMounted",
          label: $localize`:@@checklistMonitoring.centralStationIncluded.option1:Desk mounted`,
        },
        {
          value: "wallMounted",
          label: $localize`:@@checklistMonitoring.centralStationIncluded.option2:Wall mounted`,
        },
        {
          value: "ups",
          label: $localize`:@@checklistMonitoring.centralStationIncluded.option3:UPS`,
        },
      ],
      secondaryDisplaysIncluded: [
        {
          value: "size",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysIncluded.option1:Size`,
        },
        {
          value: "videoOnly",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysIncluded.option2:Video Only`,
        },
        {
          value: "interactive",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysIncludedoption3:Interactive`,
        },
        {
          value: "sound",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysIncluded.option4:Sound`,
        },
      ],
      secondaryDisplaysNetwork: [
        {
          value: "pointToPoint",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysNetwork.option1:Point to point`,
        },
        {
          value: "overIp",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysNetwork.option2:Over IP (through comms room)`,
        },
      ],
      secondaryDisplaysConnectionTo: [
        {
          value: "centralStation",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysConnectionTo.option1:Central Station`,
        },
        {
          value: "iacsPatientMonitor",
          label: $localize`:@@checklistMonitoring.secondaryDisplaysConnectionTo.option2:IACS patient monitor`,
        },
      ],
      itAppsIncluded: [
        {
          value: "gateway",
          label: $localize`:@@checklistMonitoring.itAppsIncluded.option1:Gateway`,
        },
        {
          value: "webtabs",
          label: $localize`:@@checklistMonitoring.itAppsIncluded.option2:Webtabs`,
        },
        {
          value: "itApps",
          label: $localize`:@@checklistMonitoring.itAppsIncluded.option3:IT Apps`,
        },
        {
          value: "spv",
          label: $localize`:@@checklistMonitoring.itAppsIncluded.option4:SPV`,
        },
      ],
      serverLocation: [
        {
          value: "onPrem",
          label: $localize`:@@checklistMonitoring.serverLocation.option1:On Prem`,
        },
        {
          value: "remote",
          label: $localize`:@@checklistMonitoring.serverLocation.option2:Remote`,
        },
      ],
      mountInstaller: [
        {
          value: "customer",
          label: $localize`:@@checklistMonitoring.mountInstaller.option1:Customer`,
        },
        {
          value: "draeger",
          label: $localize`:@@checklistMonitoring.mountInstaller.option2:Draeger`,
        },
      ],
      earlyItInvolvementRequired: [
        {
          value: "yes",
          label: $localize`:@@checklistMonitoring.earlyItInvolvementRequired.option1:Yes`,
        },
        {
          value: "no",
          label: $localize`:@@checklistMonitoring.earlyItInvolvementRequired.option2:No`,
        },
      ],
      commissioningRequirementsProvided: [
        {
          value: "installationOnly",
          label: $localize`:@@checklistMonitoring.commissioningRequirementsProvided.option1:Installation Only`,
        },
        {
          value: "ipm-l",
          label: $localize`:@@checklistMonitoring.commissioningRequirementsProvided.option2:IPM-L`,
        },
      ],
    };
  }

  protected readonly checklistOptions = ChecklistMonitoringComponent.getChecklistOptions();

  ngOnInit() {
    this.checklistForm = this.formBuilder.group(
      {
        existingInstallBase: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.existingInstallBase),
        networked: this.buildChecklistItemSingleChoiceFormGroup(this.project.checklistMonitoring.networked),
        networkCableSupply: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.networkCableSupply),
        wirelessNetworkRequired: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.wirelessNetworkRequired),
        deviceInterfacingRequired: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.deviceInterfacingRequired),
        centralStationIncluded: this.buildChecklistItemMultipleChoiceFormGroup(
          "centralStationIncluded",
          this.project.checklistMonitoring.centralStationIncluded,
        ),
        secondaryDisplaysIncluded: this.buildChecklistItemSecondaryDisplaysFormGroup(
          "secondaryDisplaysIncluded",
          this.project.checklistMonitoring.secondaryDisplaysIncluded,
        ),
        ownPrinter: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.ownPrinter),
        itAppsIncluded: this.buildChecklistItemMultipleChoiceFormGroup(
          "itAppsIncluded",
          this.project.checklistMonitoring.itAppsIncluded,
        ),
        digitalSolutionsIncluded: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.digitalSolutionsIncluded),
        serverLocation: this.buildChecklistItemSingleChoiceFormGroup(this.project.checklistMonitoring.serverLocation),
        additionalInformation: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.additionalInformation),

        mountInstaller: this.buildChecklistItemSingleChoiceFormGroup(this.project.checklistMonitoring.mountInstaller),
        earlyChannelSupply: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.earlyChannelSupply),
        earlyItInvolvementRequired: this.buildChecklistItemSingleChoiceFormGroup(
          this.project.checklistMonitoring.earlyItInvolvementRequired,
        ),
        ipAddressRangeProvided: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.ipAddressRangeProvided),
        departmentNamesProvided: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.departmentNamesProvided),
        bedNamesProvided: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.bedNamesProvided),
        hospitalShortNameProvided: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.hospitalShortNameProvided),
        hospitalPrinterIpsProvided: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.hospitalPrinterIpsProvided),
        commissioningRequirementsProvided: this.buildChecklistItemSingleChoiceFormGroup(
          this.project.checklistMonitoring.commissioningRequirementsProvided,
        ),
        deliveryDetails: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.deliveryDetails),
        estimatedGoLiveDate: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.estimatedGoLiveDate),
        additionalInstallationInformation: this.buildChecklistItemFormGroup(
          this.project.checklistMonitoring.additionalInstallationInformation,
        ),

        deadlineRisks: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.deadlineRisks),
        technicalRisks: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.technicalRisks),
        otherRisks: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.otherRisks),

        discussionNotes: this.buildChecklistItemFormGroup(this.project.checklistMonitoring.discussionNotes),
      },
      { updateOn: "blur" },
    );

    this.checklistForm.valueChanges.subscribe((value) => this.save(value));
  }

  private buildChecklistItemFormGroup(checklistItem: ChecklistItem) {
    return this.formBuilder.group({
      checked: [checklistItem.checked],
      notes: [checklistItem.notes],
    });
  }

  private buildChecklistItemSingleChoiceFormGroup(checklistItem: ChecklistItemSingleChoice) {
    return this.formBuilder.group({
      checked: [checklistItem.checked],
      choice: new FormControl({ value: checklistItem.choice, disabled: !checklistItem.checked }),
    });
  }

  private buildChecklistItemMultipleChoiceFormGroup(key: string, checklistItem: ChecklistItemMultipleChoice) {
    const options = this.checklistOptions[key];

    const choices: Record<string, FormControl> = {};

    for (const option of options) {
      choices[option.value] = new FormControl({
        value: checklistItem.choices?.includes(option.value) ? true : "",
        disabled: !checklistItem.checked,
      });
    }

    return this.formBuilder.group({
      checked: [checklistItem.checked],
      choices: this.formBuilder.group(choices),
    });
  }

  private buildChecklistItemSecondaryDisplaysFormGroup(key: string, checklistItem: ChecklistItemSecondaryDisplays) {
    const options = this.checklistOptions[key];

    const choices: Record<string, FormControl> = {};

    for (const option of options) {
      choices[option.value] = new FormControl({
        value: checklistItem.choices?.includes(option.value) ? true : "",
        disabled: !checklistItem.checked,
      });
    }

    return this.formBuilder.group({
      checked: [checklistItem.checked],
      choices: this.formBuilder.group(choices),
      networkChoice: [checklistItem.networkChoice],
      connectionToChoice: [checklistItem.connectionToChoice],
      notes: [checklistItem.notes],
    });
  }

  private save(value: any) {
    this.mapChecklistItem(this.project.checklistMonitoring.existingInstallBase, value.existingInstallBase);
    this.mapChecklistItemSingleChoice("networked", this.project.checklistMonitoring.networked, value.networked);
    this.mapChecklistItem(this.project.checklistMonitoring.networkCableSupply, value.networkCableSupply);
    this.mapChecklistItem(this.project.checklistMonitoring.wirelessNetworkRequired, value.wirelessNetworkRequired);
    this.mapChecklistItem(this.project.checklistMonitoring.deviceInterfacingRequired, value.deviceInterfacingRequired);
    this.mapChecklistItemMultipleChoice(
      "centralStationIncluded",
      this.project.checklistMonitoring.centralStationIncluded,
      value.centralStationIncluded,
    );
    this.mapChecklistItemSecondaryDisplays(
      "secondaryDisplaysIncluded",
      this.project.checklistMonitoring.secondaryDisplaysIncluded,
      value.secondaryDisplaysIncluded,
    );
    this.mapChecklistItem(this.project.checklistMonitoring.ownPrinter, value.ownPrinter);
    this.mapChecklistItemMultipleChoice("itAppsIncluded", this.project.checklistMonitoring.itAppsIncluded, value.itAppsIncluded);
    this.mapChecklistItem(this.project.checklistMonitoring.digitalSolutionsIncluded, value.digitalSolutionsIncluded);
    this.mapChecklistItemSingleChoice("serverLocation", this.project.checklistMonitoring.serverLocation, value.serverLocation);
    this.mapChecklistItem(this.project.checklistMonitoring.additionalInformation, value.additionalInformation);

    this.mapChecklistItemSingleChoice("mountInstaller", this.project.checklistMonitoring.mountInstaller, value.mountInstaller);
    this.mapChecklistItem(this.project.checklistMonitoring.earlyChannelSupply, value.earlyChannelSupply);
    this.mapChecklistItemSingleChoice(
      "earlyItInvolvementRequired",
      this.project.checklistMonitoring.earlyItInvolvementRequired,
      value.earlyItInvolvementRequired,
    );
    this.mapChecklistItem(this.project.checklistMonitoring.ipAddressRangeProvided, value.ipAddressRangeProvided);
    this.mapChecklistItem(this.project.checklistMonitoring.departmentNamesProvided, value.departmentNamesProvided);
    this.mapChecklistItem(this.project.checklistMonitoring.bedNamesProvided, value.bedNamesProvided);
    this.mapChecklistItem(this.project.checklistMonitoring.hospitalShortNameProvided, value.hospitalShortNameProvided);
    this.mapChecklistItem(this.project.checklistMonitoring.hospitalPrinterIpsProvided, value.hospitalPrinterIpsProvided);
    this.mapChecklistItemSingleChoice(
      "commissioningRequirementsProvided",
      this.project.checklistMonitoring.commissioningRequirementsProvided,
      value.commissioningRequirementsProvided,
    );
    this.mapChecklistItem(this.project.checklistMonitoring.deliveryDetails, value.deliveryDetails);
    this.mapChecklistItem(this.project.checklistMonitoring.estimatedGoLiveDate, value.estimatedGoLiveDate);
    this.mapChecklistItem(
      this.project.checklistMonitoring.additionalInstallationInformation,
      value.additionalInstallationInformation,
    );

    this.mapChecklistItem(this.project.checklistMonitoring.deadlineRisks, value.deadlineRisks);
    this.mapChecklistItem(this.project.checklistMonitoring.technicalRisks, value.technicalRisks);
    this.mapChecklistItem(this.project.checklistMonitoring.otherRisks, value.otherRisks);

    this.mapChecklistItem(this.project.checklistMonitoring.discussionNotes, value.discussionNotes);

    this.project.lastModified = new Date();
    this.projectService.update(this.project).subscribe();
  }

  private mapChecklistItem(checklistItem: ChecklistItem, formValue: any) {
    checklistItem.notes = formValue.notes;
    checklistItem.checked = formValue.checked;
  }

  private mapChecklistItemSingleChoice(key: string, checklistItem: ChecklistItemSingleChoice, formValue: any) {
    const choiceControl = this.checklistForm.get([key, "choice"]);

    checklistItem.choice = formValue.choice;

    if (checklistItem.checked !== formValue.checked) {
      if (formValue.checked) {
        choiceControl?.enable({ emitEvent: false });

        this.checklistForm.patchValue(
          {
            [key]: {
              choice: "",
            },
          },
          {
            emitEvent: false,
          },
        );
      } else {
        checklistItem.choice = undefined;

        this.checklistForm.patchValue(
          {
            [key]: {
              choice: undefined,
            },
          },
          {
            emitEvent: false,
          },
        );

        choiceControl?.disable({ emitEvent: false });
      }
    }

    checklistItem.checked = formValue.checked;
  }

  private mapChecklistItemMultipleChoice(key: string, checklistItem: ChecklistItemMultipleChoice, formValue: any) {
    checklistItem.checked = formValue.checked;

    const choiceControl = this.checklistForm.get([key, "choices"]);

    if (formValue.checked) {
      choiceControl?.enable({ emitEvent: false });
      checklistItem.choices = formValue.choices
        ? Object.keys(formValue.choices).filter((key) => formValue.choices[key] === true)
        : undefined;
    } else {
      checklistItem.choices = undefined;

      this.checklistForm.patchValue(
        {
          [key]: {
            choices: Object.keys(choiceControl?.value).reduce<Record<string, string>>((acc, curr) => {
              acc[curr] = "";

              return acc;
            }, {}),
          },
        },
        {
          emitEvent: false,
        },
      );
      choiceControl?.disable({ emitEvent: false });
    }
  }

  private mapChecklistItemSecondaryDisplays(key: string, checklistItem: ChecklistItemSecondaryDisplays, formValue: any) {
    checklistItem.checked = formValue.checked;

    const choiceControl = this.checklistForm.get([key, "choices"]);

    if (formValue.checked) {
      choiceControl?.enable({ emitEvent: false });

      checklistItem.choices = formValue.choices
        ? Object.keys(formValue.choices).filter((key) => formValue.choices[key] === true)
        : undefined;

      checklistItem.networkChoice = formValue.networkChoice;
      checklistItem.connectionToChoice = formValue.connectionToChoice;
      checklistItem.notes = formValue.notes;
    } else {
      checklistItem.choices = undefined;
      checklistItem.networkChoice = undefined;
      checklistItem.connectionToChoice = undefined;
      checklistItem.notes = undefined;

      this.checklistForm.patchValue(
        {
          [key]: {
            choices: Object.keys(choiceControl?.value).reduce<Record<string, string>>((acc, curr) => {
              acc[curr] = "";

              return acc;
            }, {}),
            networkChoice: undefined,
            connectionToChoice: undefined,
            notes: undefined,
          },
        },
        {
          emitEvent: false,
        },
      );
      choiceControl?.disable({ emitEvent: false });
    }
  }

  protected isChecked(path: string): boolean {
    return this.checklistForm.get(path)?.value === true;
  }

  protected isControlDisabled(path: string): boolean {
    return this.checklistForm.get(path)?.disabled ?? false;
  }

  protected readonly navigator = navigator;
}
